import { BankruptcyChecklistResponse } from "../../../api/ClientFileApi";
import { ChecklistStatus, QuestionState } from "../../BankruptcyChecklist.types";
import { computeChecklistStatus } from "../../BankruptcyChecklist.utils";
import { BankruptcyConsequencesAnswerKey, BankruptcyConsequencesFormValues } from "./BankruptcyConsequences.types";

export const EMPTY_BANKRUPTCY_CONSEQUENCES: BankruptcyConsequencesFormValues = {
  hasOwnCheckingAccount: null,
  isCheckingAccountOverdrawn: null,
  hasRegisteredMobilePhone: null,
  isElectricityInOwnName: null,
  isApartmentDistrictHeated: null,
  isDistrictHeatingInOwnName: null,
};

export function mapResponseToBankruptcyConsequences(
  response: BankruptcyChecklistResponse
): BankruptcyConsequencesFormValues {
  return {
    hasOwnCheckingAccount: response.hasOwnCheckingAccount,
    isCheckingAccountOverdrawn: response.isCheckingAccountOverdrawn,
    hasRegisteredMobilePhone: response.hasRegisteredMobilePhone,
    isElectricityInOwnName: response.isElectricityInOwnName,
    isApartmentDistrictHeated: response.isApartmentDistrictHeated,
    isDistrictHeatingInOwnName: response.isDistrictHeatingInOwnName,
  };
}

export type BankruptcyConsequencesQuestionStates = Record<BankruptcyConsequencesAnswerKey, QuestionState>;

const FOLLOW_UP_PARENTS: Partial<Record<BankruptcyConsequencesAnswerKey, BankruptcyConsequencesAnswerKey>> = {
  isCheckingAccountOverdrawn: "hasOwnCheckingAccount",
  isDistrictHeatingInOwnName: "isApartmentDistrictHeated",
};

function isVisible(key: BankruptcyConsequencesAnswerKey, values: BankruptcyConsequencesFormValues): boolean {
  const parent = FOLLOW_UP_PARENTS[key];
  return parent === undefined || values[parent] === true;
}

export function getQuestionStates(values: BankruptcyConsequencesFormValues): BankruptcyConsequencesQuestionStates {
  const entries = (Object.keys(values) as BankruptcyConsequencesAnswerKey[]).map(
    (key) => [key, { visible: isVisible(key, values), isWarning: false }] as const
  );

  return Object.fromEntries(entries) as BankruptcyConsequencesQuestionStates;
}

export function getBankruptcyConsequencesStatus(values: BankruptcyConsequencesFormValues): ChecklistStatus {
  return computeChecklistStatus(getQuestionStates(values), values);
}

export function clearUnreachableAnswers(values: BankruptcyConsequencesFormValues): BankruptcyConsequencesFormValues {
  const next = { ...values };

  for (const key of Object.keys(FOLLOW_UP_PARENTS) as BankruptcyConsequencesAnswerKey[]) {
    if (!isVisible(key, next)) {
      next[key] = null;
    }
  }

  return next;
}

export function applyAnswer(
  values: BankruptcyConsequencesFormValues,
  key: BankruptcyConsequencesAnswerKey,
  value: boolean | null
): BankruptcyConsequencesFormValues {
  return clearUnreachableAnswers({ ...values, [key]: value });
}
